import { useState } from "react";

import { typedQueryClient, useTypedMutation } from "hooks";
import { useManufacturerDashboardFilters } from "modules/ManufacturerDashboard/hooks/useManufacturerDashboardFilters";
import { ManufacturerQueryKeys } from "modules/ManufacturerDashboard/queryKeys";
import { ApiInstance } from "utils/Api";

import type { InterventionID, ManufacturerRequestID, Pin, QuestionResultID } from "models";

type ManufacturerAccessRequestPayload = {
  reason: string;
  intervention_id?: InterventionID;
  question_result_id?: QuestionResultID;
};

export const useConfirmManufacturerRequest = () => {
  const [selectedRequest, setSelectedRequest] = useState<Pin | null>(null);
  const [requestReason, setRequestReason] = useState("");

  const filters = useManufacturerDashboardFilters();

  const { mutate: requestManufacturerAccess } = useTypedMutation<ManufacturerAccessRequestPayload, ManufacturerRequestID>({
    mutationFn: async payload => (await ApiInstance.post("/manufacturers/requests/access", payload)).data.id,
    onSuccess: () => typedQueryClient.invalidateQueries(ManufacturerQueryKeys.manufacturerListRequests(filters))
  });

  const selectRequest = (row: Pin) => setSelectedRequest(row);
  const handleRequestReason = (input: string) => setRequestReason(input);

  const closeConfirmModal = () => {
    setRequestReason("");
    setSelectedRequest(null);
  };

  const createManufacturerRequest = (onSuccess: () => void) => {
    if (!selectedRequest) return;

    const { intervention_id, question_result_id } = selectedRequest;
    if (question_result_id) return requestManufacturerAccess({ reason: requestReason, question_result_id }, { onSuccess });
    if (intervention_id) requestManufacturerAccess({ reason: requestReason, intervention_id }, { onSuccess });
  };

  return {
    createManufacturerRequest,
    handleRequestReason,
    closeConfirmModal,
    selectRequest,
    isConfirmButtonDisabled: !requestReason,
    selectedRequest
  };
};
